//@ts-strict-ignore
import React, { ChangeEvent, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { serviceActions } from '../../state/modules/service';
import { default as Form } from '../../components/connectedForm';
import Dropdown from '../../components/connectedForm/Dropdown';
import QueryDropdown from '../../components/connectedForm/QueryDropdown';
import getIn from '../../components/connectedForm/utils/getIn';
import getValue from '../../components/connectedForm/utils/getValue';
import { Customer, ReportTemplate, SalesInvoice, Supplier, SystemPrinter } from '../../graphql/entities';
import { emailFromEntity, IEntityEmail } from '../../utils/EmailUtils';
import Cookies from 'universal-cookie';
import { useDocumentConfig, useSalesOrderConfig } from '../../utils/FetchConfig';
import Label from '../connectedForm/Label';

import { useRangeMutationProps } from '../../actions/sales/DocumentAction';
import ModalContext from '../Context/ModalContext';
import { getAvailableReportOutputTypes } from '../../utils/getAvailableReportOutputTypes';
import { getDefaultOutputType } from '../../utils/getDefaultOutputType';
import Wysiwyg from '../connectedForm/Wysiwyg';
import getClientMutationId from '../../utils/getClientMutationId';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import AppContext from '../Context/AppContext';
import ContactEmailEditor from '../report/ContactEmailEditor';
import {
  AlertType,
  FilterOperation,
  IAbstractDocumentDefault,
  IAcknowledgementDefault,
  ICommercialInvoiceDocumentDefault,
  IConformityCertificateDefault,
  ICustomer,
  ICustomerContact,
  IDataSheetDocumentDefault,
  IDebitNoteDocumentDefault,
  IDeliveryNoteDefault,
  IDispatchEmailDefault,
  IDocumentConfig,
  IFilterInput,
  IJobInfo,
  IJobProgress,
  IOrderConfig,
  IPaymentReceiptDefault,
  IPickingInstructionDefault,
  IProductLabelBasicDefault,
  IProductLabelDefault,
  IProformaInvoiceDocumentDefault,
  IPurchaseOrderDocumentDefault,
  IQuotationDocumentDefault,
  IReceiptLabelDocumentDefault,
  IRemittanceDocumentDefault,
  IReportParametersInput,
  IReportTemplateInput,
  ISalesCreditDocumentDefault,
  ISalesInvoiceDocumentDefault,
  ISalesReturnNoteDocumentDefault,
  IStatementDocumentDefault,
  ISupplier,
  ISupplierContact,
  JobType,
  Maybe,
  ReportOutputType,
  ReportTemplateFormat,
} from '../../graphql/schema/p4types';
import MergeFieldContext from '../../components/Context/MergeFieldContext';
import ConnectedFormContext, { IConnectedFormModalProps } from '../Context/ConnectedFormContext';
import { EntityType, IExtendedDocumentDefault } from '../../@types/entityTypes';
import CheckboxInput from '../connectedForm/CheckboxInput';
import CheckPopUpBlockerFunction from '../../utils/CheckPopUpBlockerFunction';
import TradeCounterNoSaleMutation from '../../graphql/mutations/sales/TradeCounterNoSaleMutation.graphql';
import { handleComponentQueryError } from '../../utils/parseErrorResponse';
import ToggleInput from '../connectedForm/ToggleInput/ToggleInput';
import { IJobMeta } from '../../utils/BackgroundJobs';
import { IStyleFunctionOrObject } from '@fluentui/react';
import { BasicHeader } from '../../actions/BasicHeader';
import { ModalBottomButtonContainer } from './Styles';
import { PrimaryButton, SecondaryButton, WarningButton } from '../connectedForm/Button';
import { useHistory } from 'react-router';
import DocumentModalContext from '../Context/DocumentModalContext';
import { IModalStyleProps, IModalStyles } from '@fluentui/react/src/components/Modal/Modal.types';
import { useCookies } from 'react-cookie';
import { add } from 'date-fns';
import { useOnProcessPreview } from './EmailPrintPreviewModal';
import { Spinner, Tooltip } from '@fluentui/react-components';
import { Analytics } from '../../mixpanel';
import FluentModal from './FluentModal';

const DOCUMENT_SUMMARY = 'documentSummary';

const cookies = new Cookies();

const PRINT_INDIVIDUAL_PREFERENCE_COOKIE = 'PRINT_INDIVIDUAL_PREFERENCE_COOKIE';
const OPEN_TILL_DRAWER_COOKIE = 'OPEN_TILL_DRAWER_COOKIE';

/**
 * Temporary wrapper for getting document name. Eventually should just need the [get(entity, 'configuration.field')] part...
 */
const getDocumentName: (entity: EntityType) => keyof IDocumentConfig | undefined = (entity) => {
  return entity.configuration?.field;
};

export type IEmailConfig = {
  emailSubject: Maybe<string>;
  emailTo: Array<IEntityEmail>;
  emailCc: Array<IEntityEmail>;
  emailFrom: Maybe<string>;
};

export type IDocumentModalState = {
  outputType: Maybe<ReportOutputType>;
  printerId: Maybe<number>;
  template: Maybe<IReportTemplateInput>;
  emailConfig: IEmailConfig;
  message: string;
  overrideTemplate: boolean; //For single entry, always permit override (Since we can see the default values already).
  overrideOutput: boolean; //For single entry, always permit override (Since we can see the default values already).
  recipient: ICustomer | ISupplier; //In state for comparison in getDerivedStateFromProps;
  documentDefault: IDocumentDefault | IAbstractDocumentDefault | undefined; //In state for comparison in getDerivedStateFromProps;
  showSummary: boolean;
  waiting: boolean;
  printIndividually: boolean;
  printContiguous: boolean;
  recipientForEmail: ICustomer | ISupplier;
} & { [key: string]: any }; //Arbitrary additional state possible with extension components (Via children or otherwise).

const getDefaultState: (
  options: { multiValue?: boolean; entity: EntityType; useCustomerDefaults?: boolean; allowEntityEmail?: boolean },
  documentDefault: IDocumentDefault | IAbstractDocumentDefault | undefined,
  user: any,
  recipient: ICustomer | ISupplier,
  printContiguous: string,
  salesOrderConfig: Maybe<IOrderConfig>
) => IDocumentModalState = (
  { multiValue, entity, useCustomerDefaults, allowEntityEmail },
  documentDefault = {} as any,
  user,
  recipient,
  printContiguous,
  salesOrderConfig
) => {
  const receiveFlag: string = getIn(entity, 'configuration.receiveFlag');

  //Recipient = The Customer or Supplier Record.
  let calculatedDefault: IExtendedDocumentDefault = documentDefault;

  const documentName = getDocumentName(entity);

  let recipientForEmail = recipient;

  if (recipient && useCustomerDefaults && documentName) {
    let recipientDocumentDefault = getIn(
      recipient,
      entity.configuration?.customerField || entity.configuration?.supplierField
    );
    let tempDocumentDefault;
    if (recipientDocumentDefault && recipientDocumentDefault[documentName]) {
      tempDocumentDefault = recipientDocumentDefault[documentName];
    } else {
      console.warn(`No document default defined for '${documentName}'`);
      tempDocumentDefault = {};
      recipientDocumentDefault = {}; //Set a blank document default or we'll get an exception...
    }

    let recipientOtherContacts = getIn(recipient, 'contacts') //Contacts marked to receive this document type
      ? getIn(recipient, 'contacts').filter((contact: ICustomerContact | ISupplierContact) => {
          return contact[receiveFlag as keyof typeof contact];
        })
      : [];

    if (
      salesOrderConfig?.documentPreferencesFromHeadOffice &&
      !recipientDocumentDefault.layout &&
      recipientOtherContacts.length === 0 &&
      entity.configuration?.customerField &&
      recipient.headOffice
    ) {
      const headOfficeDocumentDefault = getIn(recipient.headOffice, entity.configuration?.customerField);
      if (headOfficeDocumentDefault) {
        recipientForEmail = recipient.headOffice;
        recipientDocumentDefault = headOfficeDocumentDefault;
        recipientOtherContacts = getIn(recipient.headOffice, 'contacts')
          ? getIn(recipient.headOffice, 'contacts').filter((contact: ICustomerContact) => {
              return contact[receiveFlag as keyof typeof contact];
            })
          : [];
      }
    }

    calculatedDefault = {
      ...tempDocumentDefault,
      documentRequired: recipientDocumentDefault.documentRequired || documentDefault.documentRequired,
      output: recipientDocumentDefault.output || documentDefault.output,
      systemPrinterId: recipientDocumentDefault.systemPrinterId || documentDefault.systemPrinterId,
      layout: recipientDocumentDefault.layout || documentDefault.layout,
      emailList: emailFromEntity(recipientForEmail, undefined, recipientOtherContacts, { allowEntityEmail }),
      subject: recipientDocumentDefault.subject || documentDefault.subject,
    };
  }

  let showSummary = false;
  if (multiValue && cookies.get(DOCUMENT_SUMMARY)) {
    showSummary = cookies.get(DOCUMENT_SUMMARY) === 'true'; // Note, cookie stored as text
  }
  const template = (calculatedDefault.layout || documentDefault?.layout) as Maybe<IReportTemplateInput>;
  const state: IDocumentModalState = {
    outputType: calculatedDefault.output || getDefaultOutputType(template?.reportTemplateFormat).value,
    printerId: calculatedDefault.systemPrinterId,
    template,
    emailConfig: {
      emailSubject: calculatedDefault.subject,
      emailTo: calculatedDefault.emailList || [],
      emailCc: [],
      emailFrom: documentDefault.usersEmailAsFromAddress ? user.email : documentDefault.fromAddress,
    },
    message: documentDefault.body ? documentDefault.body : '',
    overrideTemplate: !multiValue, //For single entry, always permit override (Since we can see the default values already).
    overrideOutput: !multiValue, //For single entry, always permit override (Since we can see the default values already).
    recipient: recipient, //In state for comparison in getDerivedStateFromProps;
    documentDefault: documentDefault, //In state for comparison in getDerivedStateFromProps;
    showSummary: showSummary,
    waiting: false,
    printIndividually: false,
    printContiguous: printContiguous === 'true',
    recipientForEmail: recipientForEmail,
  };

  return state;
};

export const WaitingForSummary = ({ skip }: { skip: () => void }) => {
  return (
    <>
      <BasicHeader>Generating Summary</BasicHeader>
      <div style={{ padding: '1em' }}>
        <Spinner size="huge" label={'Waiting for Documents'} />
        <ModalBottomButtonContainer>
          <SecondaryButton onClick={skip}>Skip Summary</SecondaryButton>
        </ModalBottomButtonContainer>
      </div>
    </>
  );
};

const SystemPrinterConfiguration = ({
  documentState,
  onChange,
}: {
  documentState: IDocumentModalState;
  onChange: Function;
}) => {
  const { outputType, printerId } = documentState;

  if (outputType !== ReportOutputType.SystemPrinter) {
    return null;
  }

  return (
    <Form.Fieldset cols={1}>
      <QueryDropdown
        {...SystemPrinter.dropdown}
        label="System Printer"
        onChange={(value: number) => onChange('printerId', value)}
        value={printerId}
        valueKey="id"
        labelKey="printerName"
        valueOnly
        data-testid={'document-modal-system-printer'}
      />
    </Form.Fieldset>
  );
};

/**
 * Wrapper to encompass in a Modal, where the parent component has not already provided this functionality.
 */
const ModalAndStyleWrapper = ({
  children,
  modalStyles,
  modalOpen,
  modalWrapper,
  noModal,
  multiValue,
}: {
  children: React.ReactElement;
  modalStyles?: IStyleFunctionOrObject<IModalStyleProps, IModalStyles> & { layer?: { root?: React.CSSProperties } };
  modalOpen?: boolean;
  modalWrapper?: boolean;
  noModal?: boolean;
  multiValue?: boolean;
}) => {
  if ((modalWrapper || !multiValue) && !noModal) {
    return (
      <FluentModal
        isOpen={modalOpen}
        styles={{
          main: { minWidth: '700px', paddingBottom: '60px', overflowY: 'unset' },
          scrollableContent: { maxHeight: '97vh' },
          ...modalStyles,
        }}
      >
        {children}
      </FluentModal>
    );
  }

  return children;
};

type IDocumentDefault =
  | IPaymentReceiptDefault
  | IProductLabelDefault
  | IPurchaseOrderDocumentDefault
  | IAcknowledgementDefault
  | IDeliveryNoteDefault
  | IStatementDocumentDefault
  | IConformityCertificateDefault
  | IDataSheetDocumentDefault
  | IProformaInvoiceDocumentDefault
  | IQuotationDocumentDefault
  | IDispatchEmailDefault
  | IProductLabelBasicDefault
  | IDebitNoteDocumentDefault
  | ISalesInvoiceDocumentDefault
  | ISalesCreditDocumentDefault
  | ISalesReturnNoteDocumentDefault
  | IRemittanceDocumentDefault
  | IReceiptLabelDocumentDefault
  | IPickingInstructionDefault
  | ICommercialInvoiceDocumentDefault;

export type IOnProcessSummary = (
  skipped?: boolean,
  { showSummary }?: { showSummary: boolean },
  _?: any, //Unused?
  job?: IJobInfo | IJobProgress | IJobMeta,
  failed?: boolean
) => void;

export const useOnProcessSummary = (onClose?: Function): IOnProcessSummary => {
  return (skipped, state, _, job, failed = false) => {
    if (failed) {
      onClose?.();
      console.warn('Job unsuccessful');
    }
    if (!skipped && state?.showSummary) {
      window.open(`/Company/DocumentSummary/Workspace/${job!.jobId}`, '_blank');
    }
  };
};

type IDocumentModal2Props = {
  modalStyles?: any;
  entity: EntityType;
  values?: any;
  recipientField?: 'customer' | 'supplier' | 'salesOrder.customer';
  contactField?: 'customerContact' | 'supplierContact' | null;
  module?: 'Sales' | 'Purchases';
  onClose?: Function;
  reprint?: boolean;
  additionalInputVariables?: any;
  type?: string;
  title?: string;
  returnUrl?: string | null;
  multiValue?: boolean;
  allowEmail?: boolean;
  count?: number;
  useCustomerDefaults?: boolean;
  outputSelection?: any;
  allowEmailTo?: boolean;
  children?: any;
  allowEntityEmail?: boolean;
  allowOpenTillDrawer?: boolean;
  allowResetToStart?: boolean;
  documentConfig: Maybe<IDocumentConfig>;
  onComplete?: () => void; //A function to perform when the print operation has completed.
  delayBeforeSkip?: boolean;
  generationMutation?: DocumentNode; //@Deprecated - Provision for 'remittance' documents - Do not use for new document logic.
  mutationKey?: string; //@Deprecated - Provision for 'remittance' documents.
  actionEntities?: Array<any>;
  onProcessSummary?: IOnProcessSummary;
  prePrintAction?: () => void; //provide a callback which executes before the print process.
  onPrintClick?: (meta: {
    reportParameters: IReportParametersInput;
    documentState: IDocumentModalState;
    setWaiting: React.Dispatch<React.SetStateAction<boolean>>;
  }) => void; //Override the behaviour when 'print' is clicked - Allow parent component to provide this rather than 'printFunctions' on the entity record.
  headerProps?: { secondaryTitle?: ReactNode };
  extraData?: any; //May be used by children, not expected for direct use.
  disableIndividual?: boolean;
  overrideActionProps?: any;
  blockNavigation?: boolean;
  allowEmailNoDefaultRecipient?: boolean;
} & Partial<IConnectedFormModalProps>;

const DocumentModal2 = (props: IDocumentModal2Props) => {
  const {
    modalStyles: modalStylesFromProps,
    entity,
    documentConfig,
    values,
    recipientField = 'customer',
    onClose: onCloseFromProps,
    reprint,
    additionalInputVariables,
    type: typeFromProps,
    title,
    returnUrl,
    multiValue,
    allowEmail,
    count,
    useCustomerDefaults,
    outputSelection,
    allowEmailTo,
    children,
    allowEntityEmail,
    /**
     * Whether the main email from the customer/supplier should be used if no other emails are set.
     * (Note - Only used for Sales Invoices and only from the Trade Couner).
     */
    allowOpenTillDrawer,
    allowResetToStart,
    onComplete: onCompleteFromProps,
    disableIndividual,
    allowEmailNoDefaultRecipient,
  } = props;

  const salesOrderConfig = useSalesOrderConfig();

  // Dont allow till drawer to be opened if the count of invoices selected is greater than 1.
  let askIfTillDrawerShouldBeOpened = allowOpenTillDrawer;
  if (props.count !== undefined && props.count > 1) {
    askIfTillDrawerShouldBeOpened = false;
  }

  const documentName = getDocumentName(entity);

  const defaultConfig: IDocumentDefault | IAbstractDocumentDefault | undefined =
    ((documentName && documentConfig?.[documentName]) as IDocumentDefault) || entity.configuration?.defaultConfig;
  const recipientFromProps = getIn(values, recipientField);

  const { data: recipientData } = useQuery(
    (recipientField === 'customer' || recipientField === 'salesOrder.customer'
      ? Customer.documentDefault
      : Supplier.instance) as DocumentNode,
    {
      variables:
        recipientField === 'customer' || recipientField === 'salesOrder.customer'
          ? { id: recipientFromProps?.id, includeHeadOffice: true }
          : { id: recipientFromProps?.id },
      skip: !recipientFromProps?.id,
    }
  );

  const [noSale] = useMutation(TradeCounterNoSaleMutation, {
    onError: (data) => handleComponentQueryError(data, { dispatch, entity: null }, 'Open Drawer request failed'),
  });

  const recipient = recipientData?.data || recipientFromProps;
  const dispatch = useDispatch();
  const app = useContext(AppContext);
  const [cookie, setCookie] = useCookies([PRINT_INDIVIDUAL_PREFERENCE_COOKIE, OPEN_TILL_DRAWER_COOKIE]);
  const defaultState = getDefaultState(
    { multiValue, entity, useCustomerDefaults, allowEntityEmail },
    defaultConfig,
    app.user,
    recipient,
    cookie[PRINT_INDIVIDUAL_PREFERENCE_COOKIE],
    salesOrderConfig
  );
  const [documentState, setDocumentState] = useState<IDocumentModalState>(defaultState);
  const [waiting, setWaiting] = useState(false);
  const [resetToStart, setResetToStart] = useState(false);
  const didMount = useRef(false);
  const connectedForm = useContext(ConnectedFormContext);

  const { onClose: closeFromContext } = useContext(ModalContext);
  const onClose = closeFromContext || onCloseFromProps; //'onCloseFromProps' to be deprecated, but there's still places using this approach currently.

  const history = useHistory();

  const getReportParameters = (): IReportParametersInput => {
    let { emailTo, emailCc } = documentState.emailConfig;
    emailTo = emailTo || [];
    emailCc = emailCc || [];

    const jobType: JobType = reprint ? JobType.Reprint : JobType.New;

    const outputType =
      (documentState.outputType as Maybe<string>) === 'PDF_ZIP' ? ReportOutputType.Pdf : documentState.outputType;

    return {
      emailToList: emailTo.map((email) => email.email).toString(),
      emailCcList: emailCc.map((email) => email.email).toString(),
      emailSubject: documentState.emailConfig.emailSubject,
      emailFrom: documentState.emailConfig.emailFrom,
      emailBody: documentState.message,
      template: documentState.template,
      outputType: outputType,
      printerId: documentState.printerId,
      overrideTemplate: documentState.overrideTemplate,
      overrideOutput: documentState.overrideOutput,
      printIndividually: documentState.printIndividually,
      printContiguous: documentState.printContiguous,
      jobType: jobType,
      resetToStart: resetToStart,
    };
  };

  const getDefaultMutationProps = () => ({
    clientMutationId: getClientMutationId(),
    reportParameters: getReportParameters(),
    ...additionalInputVariables,
  });

  /**
   * Trigger a form data reload when the document modal print operation is completed.
   */
  const onComplete = useCallback(() => {
    connectedForm.concurrencyReloadData?.();
    onCompleteFromProps?.();
  }, [connectedForm, onCompleteFromProps]);

  //Better solve might be an implementation in the individual generate functions, but for now..
  //If 'showSummary' is false (I.e. single print) pass a mock for onClose, so we don't call it twice.
  const propsForPrinting: any = documentState.showSummary
    ? { ...props, onClose: onClose, onComplete: onComplete, dispatch, history }
    : {
        ...props,
        resetToStart,
        onClose: () => {},
        onComplete: onComplete,
        dispatch,
        history,
      };
  const defaultMutationProps = getDefaultMutationProps();

  //New Approach - Use a hook to generate action parameters so we can get filters/actionEntities directly from workspace context.
  //TODO - Migrate to 'onPrintClick' instead - Let the implementing component handle the mutation call.
  //     - Note this can be assisted via the 'usePrintMutation' hook.
  const rangeMutationProps = useRangeMutationProps(
    documentState,
    propsForPrinting,
    defaultMutationProps,
    typeFromProps
  );

  useEffect(() => {
    //Note: Uses the didMount ref to avoid needing to set defaultState on initial mount, as it's already defaulted into state.
    if (didMount.current) {
      setDocumentState(defaultState);
    }
    didMount.current = true;
  }, [defaultConfig, recipient]);

  const templateFilters: Array<IFilterInput> = [
    {
      filterOperation: FilterOperation.Equals,
      filterParameters: {
        field: 'reportType',
        value: entity.configuration?.reportType,
      },
    },
  ];

  const doClose = ({ entityType }: { entityType: EntityType }) => {
    if (allowOpenTillDrawer && cookie[OPEN_TILL_DRAWER_COOKIE] === 'true' && entityType === SalesInvoice) {
      noSale({
        variables: {
          input: {
            clientMutationId: getClientMutationId(),
          },
        },
      }).then(({ data }) => {
        if (data?.tradeCounterTerminalNoSale?.result) {
          const newWindow = CheckPopUpBlockerFunction(
            { dispatch },
            `/api/resources/report/${data.tradeCounterTerminalNoSale.result}`,
            '_blank'
          );

          if (newWindow) {
            newWindow.print();
          }
        }
      });
    }

    onClose?.(props);
  };

  const print = () => {
    const notificationTitle = `${title} could not be output`;
    if (documentState.template === null || documentState.template === undefined) {
      dispatch(
        serviceActions.genericAlert(
          notificationTitle,
          'No default template configured, a default or override is required',
          AlertType.Warning
        )
      );
      return;
    }
    if (documentState.outputType === null || documentState.outputType === undefined) {
      dispatch(
        serviceActions.genericAlert(
          notificationTitle,
          'No default output configured, a default or override is required',
          AlertType.Warning
        )
      );
      return;
    }
    if (
      documentState.outputType === ReportOutputType.SystemPrinter &&
      (documentState.printerId === null || documentState.printerId === undefined)
    ) {
      dispatch(serviceActions.genericAlert(notificationTitle, 'Please select a system printer', AlertType.Warning));
      return;
    }

    if (
      allowEmailTo &&
      !props.multiValue &&
      documentState.outputType === ReportOutputType.Email &&
      !(documentState.emailConfig.emailTo?.length > 0)
    ) {
      dispatch(
        serviceActions.genericAlert(
          notificationTitle,
          'For sending a single email, one or more emails must be provided.',
          AlertType.Warning
        )
      );
      return;
    }

    if (returnUrl === undefined) {
      console.warn(`DocumentModal for ${notificationTitle} is not setting a return URL. This may have implications.`);
    }

    if (props.prePrintAction) {
      props.prePrintAction();
    }

    if (props.onPrintClick) {
      //If 'onPrintClick' is provided, implementing component is handling the mutation so we don't need to call printFunctions.
      props.onPrintClick({ reportParameters: getReportParameters(), documentState, setWaiting });
    } else if (multiValue) {
      if (reprint) {
        entity.printFunctions?.reprintRange?.(
          documentState,
          propsForPrinting,
          defaultMutationProps,
          returnUrl || 'back',
          {
            rangeMutationProps,
          }
        );
      } else {
        entity.printFunctions?.generateRange?.(
          documentState,
          propsForPrinting,
          defaultMutationProps,
          returnUrl || 'back',
          {
            rangeMutationProps,
          }
        );
      }
    } else {
      entity.printFunctions?.generate?.(documentState, propsForPrinting, defaultMutationProps, returnUrl);
    }

    if (multiValue) {
      cookies.set(DOCUMENT_SUMMARY, documentState.showSummary, { path: '/' });
    }

    // Track user print options
    Analytics.track('Report printed', {
      documentType: entity.type,
      outputType: documentState.outputType,
      template: documentState.template?.name,
      overrideTemplate: documentState.overrideTemplate,
      overrideOutput: documentState.overrideOutput,
      showSummary: documentState.showSummary,
      printIndividually: documentState.printIndividually,
      printContiguous: documentState.printContiguous,
    });

    if (documentState.showSummary && !props.onPrintClick) {
      //onPrintClick handles showSummary if required - Do not set the DocumentModal to waiting state.
      setWaiting(true);
    } else {
      doClose({ entityType: entity });
    }
  };
  const getAvailableOutputTypes = (format: ReportTemplateFormat): { value: string; label: string }[] => {
    let availableTemplateOptions = outputSelection ? outputSelection : getAvailableReportOutputTypes(format);

    if ((props.count === undefined || props.count > 1) && format === ReportTemplateFormat.Jasper) {
      availableTemplateOptions = [...availableTemplateOptions];
    }

    return availableTemplateOptions;
  };

  const onTemplateChange = (option?: any) => {
    const message = option?.templateContent || defaultConfig?.body || '';
    const updatedState = {
      ...documentState,
      template: option,
      message: message,
    };

    const format = option?.reportTemplateFormat;
    const availableTemplateOptions = getAvailableOutputTypes(format);

    if (!availableTemplateOptions.some((value) => value.value === documentState.outputType)) {
      //If the selected template doesn't support the selected outputType, need to reset it to one that is valid.
      if (format === ReportTemplateFormat.Handlebars) {
        updatedState.outputType = ReportOutputType.Email;
      } else {
        updatedState.outputType = ReportOutputType.LocalPrinter;
      }
    }

    setDefaultMessage(message);
    setDocumentState(updatedState);
  };

  const onChange = (id: string, option: any) => {
    setDocumentState({ ...documentState, [id]: option });
    if (id === 'printContiguous') {
      setCookie(PRINT_INDIVIDUAL_PREFERENCE_COOKIE, option, {
        expires: add(new Date(), { years: 1 }),
      });
    }
  };

  const onEmailUpdate = (emailConfig: IEmailConfig) => {
    setDocumentState({ ...documentState, emailConfig });
  };

  const {
    overrideTemplate,
    overrideOutput,
    outputType,
    template,
    message,
    showSummary,
    printIndividually,
    printContiguous,
  } = documentState;

  const [defaultMessage, setDefaultMessage] = useState(message);

  const skip = () => {
    onClose?.(true);
  };

  //Note - 'connectedForm.disableModalClose' is an older mechanism, to be phased out.
  //(Best to let the modal handle it's own state, rather than updating context that would trigger unnecessary extra renders on other components).
  const [disableModalClose, setDisableModalClose] = useState(props.delayBeforeSkip);
  useEffect(() => {
    if (disableModalClose) {
      setTimeout(() => setDisableModalClose(false), 1500);
    }
  }, [disableModalClose]);

  const disableModalCloseOption = disableModalClose || connectedForm.disableModalClose;

  if (waiting) {
    return <WaitingForSummary skip={skip} />;
  }

  let printAction = 'Send/Print';
  if (overrideOutput) {
    switch (outputType) {
      case ReportOutputType.Email:
        printAction = 'Send';
        break;
      default:
        printAction = 'Print';
    }
  }

  const documentModalTitle = entity.configuration?.title || entity.title;

  const mergeFields =
    entity.mergeFields ||
    fieldMetaData.ReportType.enumValues.find((enumValue) => enumValue.value === template?.reportType)?.mergeFields ||
    [];

  const handleOpenTillDrawerCheckboxChange = (changeEvent: ChangeEvent<HTMLInputElement>) => {
    setCookie(OPEN_TILL_DRAWER_COOKIE, getValue(changeEvent), {
      expires: add(new Date(), { years: 1 }),
    });
  };

  const handleResetToStartCheckboxChange = (changeEvent: ChangeEvent<HTMLInputElement>) => {
    setResetToStart(getValue(changeEvent));
  };

  let printButton: ReactElement;
  if (count === 0) {
    //Note - div inside the Tooltip is required for correct positioning. (Alternatively can add forwardRefs through Button components).
    printButton = (
      <Tooltip relationship={'label'} content={'No records have been selected, report may be empty.'}>
        <div>
          <WarningButton
            onClick={(e) => {
              e.preventDefault();
              if (!disableModalCloseOption) {
                print();
              }
            }}
            disabled={disableModalCloseOption || (recipientFromProps?.id && !recipient)}
            data-testid={`${documentModalTitle.toLowerCase()}-print`}
          >
            {printAction}
          </WarningButton>
        </div>
      </Tooltip>
    );
  } else {
    printButton = (
      <PrimaryButton
        onClick={(e) => {
          e.preventDefault();
          if (!disableModalCloseOption) {
            print();
          }
        }}
        disabled={disableModalCloseOption || (recipientFromProps?.id && !recipient)}
        data-testid={`${documentModalTitle.toLowerCase()}-print`}
      >
        {printAction}
      </PrimaryButton>
    );
  }

  let preventEmailEdit = (!allowEmail && multiValue) || outputType !== ReportOutputType.Email || !recipientData?.data;
  if (preventEmailEdit && allowEmailNoDefaultRecipient && outputType === ReportOutputType.Email) {
    preventEmailEdit = false;
  }

  // style with marginRight here prevents horizontal scrollbar appearing
  return (
    <>
      <BasicHeader secondaryTitle={props.headerProps?.secondaryTitle}>
        {count !== undefined
          ? `${printAction} ${documentModalTitle.toLowerCase()} (${count} records)`
          : `${printAction} ${documentModalTitle.toLowerCase()}`}
      </BasicHeader>
      <div style={{ padding: '1em' }}>
        <Form.Row style={{ marginLeft: 0, marginRight: 0 }}>
          {multiValue && useCustomerDefaults ? (
            <Form.Fieldset cols={1 / 3}>
              <ToggleInput
                dataid="overrideTemplate"
                label="Override Template"
                value={overrideTemplate}
                onChange={(value) => onChange('overrideTemplate', getValue(value))}
                labelCols={4 / 5}
                inputCols={1 / 5}
                hint={'If not set, customer defaults will be used.'}
              />
            </Form.Fieldset>
          ) : null}
          {multiValue && !overrideTemplate && useCustomerDefaults ? null : (
            <Form.Fieldset cols={2 / 3}>
              <QueryDropdown
                {...ReportTemplate.dropdown}
                label="Template"
                onChange={onTemplateChange}
                value={template}
                predefinedFilters={templateFilters}
                readOnly={!overrideTemplate && useCustomerDefaults}
                data-testid={'document-modal-template'}
              />
            </Form.Fieldset>
          )}
        </Form.Row>
        <Form.Row style={{ marginLeft: 0, marginRight: 0 }}>
          {multiValue && useCustomerDefaults ? (
            <Form.Fieldset cols={1 / 3}>
              <ToggleInput
                dataid="overrideOutput"
                label="Override Output"
                value={overrideOutput}
                onChange={(value) => onChange('overrideOutput', getValue(value))}
                labelCols={4 / 5}
                inputCols={1 / 5}
                hint={'If not set, customer defaults will be used.'}
              />
            </Form.Fieldset>
          ) : null}
          {multiValue && !overrideOutput && useCustomerDefaults ? null : (
            <Form.Fieldset cols={2 / 3}>
              <Dropdown
                testId="send-to-output-input"
                dataid="outputType"
                options={outputSelection ? outputSelection : getAvailableOutputTypes(template?.reportTemplateFormat!)}
                label="Output"
                onChange={(value: any) => onChange('outputType', value)}
                value={outputType}
                valueKey="value"
                labelKey="label"
                valueOnly
                readOnly={!overrideOutput && useCustomerDefaults}
                clearable={false}
                rawLabel
              />
            </Form.Fieldset>
          )}
        </Form.Row>
        {multiValue && useCustomerDefaults ? (
          <Form.Fieldset cols={1 / 3}>
            <ToggleInput
              dataid="showSummary"
              label="Show Summary"
              value={showSummary}
              onChange={(value: boolean) => onChange('showSummary', getValue(value))}
              labelCols={4 / 5}
              inputCols={1 / 5}
              hint={"Display summary of document's produced"}
            />
          </Form.Fieldset>
        ) : null}
        {!disableIndividual &&
        (!count || count > 1) &&
        (outputType === ReportOutputType.LocalPrinter || outputType === ReportOutputType.Email) ? (
          <Form.Fieldset cols={1 / 3}>
            <ToggleInput
              label={`${outputType === ReportOutputType.LocalPrinter ? 'Print' : 'Send'} Individually`}
              labelCols={4 / 5}
              inputCols={1 / 5}
              value={printIndividually}
              onChange={(event) => onChange('printIndividually', getValue(event))}
              hint={
                outputType === ReportOutputType.LocalPrinter
                  ? 'Documents will be generated individually and be downloaded as a ZIP file'
                  : 'Documents will be generated individually and sent in individual e-mails'
              }
              data-testid="document-modal-print-individually"
            />
          </Form.Fieldset>
        ) : null}
        {!disableIndividual &&
        printIndividually &&
        !overrideOutput &&
        (!count || count > 1) &&
        (outputType === ReportOutputType.LocalPrinter || outputType === ReportOutputType.Email) ? (
          <Form.Fieldset cols={2 / 3}>
            <Dropdown
              testId="print-contiguous"
              dataid="printContiguous"
              options={[
                {
                  value: false,
                  label: 'Print / Send All Individually',
                },
                {
                  value: true,
                  label: 'Print Together / Send Individually',
                },
              ]}
              label="Preference"
              onChange={(value: any) => onChange('printContiguous', value)}
              value={printContiguous}
              valueKey="value"
              labelKey="label"
              valueOnly
              clearable={false}
              rawLabel
            />
          </Form.Fieldset>
        ) : null}
        {askIfTillDrawerShouldBeOpened ? (
          <Form.Fieldset cols={1}>
            <CheckboxInput
              label="Open Till Drawer"
              labelCols={3 / 12}
              inputCols={9 / 12}
              value={cookie[OPEN_TILL_DRAWER_COOKIE] === 'true'}
              onChange={handleOpenTillDrawerCheckboxChange}
              data-testid={'document-modal-open-till-drawer'}
            />
          </Form.Fieldset>
        ) : null}
        {allowResetToStart ? (
          <Form.Fieldset cols={1}>
            <CheckboxInput
              label="Reset to all items"
              labelCols={3 / 12}
              inputCols={9 / 12}
              value={resetToStart}
              onChange={handleResetToStartCheckboxChange}
              hint={'Reset any back orders to include all order lines on the delivery note'}
              data-testid={'document-modal-reset-to-all-items'}
            />
          </Form.Fieldset>
        ) : null}
        <SystemPrinterConfiguration documentState={documentState} onChange={onChange} />
        {preventEmailEdit ? null : (
          <ContactEmailEditor
            defaultEmailConfig={defaultState.emailConfig}
            allowEmailTo={allowEmailTo}
            allowEmailCc={true}
            onUpdate={onEmailUpdate}
            commonRecord={defaultState.recipientForEmail}
          />
        )}
        {outputType === ReportOutputType.Email ||
        outputType === ReportOutputType.EmailWithPreview ||
        template?.reportTemplateFormat === ReportTemplateFormat.Handlebars ? (
          <MergeFieldContext.Provider value={{ mergeFields }}>
            <div style={{ marginTop: '12px' }} />
            <Form.Fieldset cols={1}>
              <Label>Message</Label>
              <Wysiwyg
                onChange={(value) => {
                  onChange('message', value);
                }}
                defaultValue={defaultMessage}
                value={message}
                allowImages={true}
                allowColorPicker={true}
              />
            </Form.Fieldset>
          </MergeFieldContext.Provider>
        ) : null}
        <DocumentModalContext.Provider value={{ onChange, values: documentState }}>
          {children}
        </DocumentModalContext.Provider>
        <ModalBottomButtonContainer>
          <SecondaryButton
            onClick={disableModalCloseOption ? undefined : skip}
            disabled={disableModalCloseOption}
            data-testid={`${documentModalTitle.toLowerCase()}-skip`}
          >
            Skip
          </SecondaryButton>
          {printButton}
        </ModalBottomButtonContainer>
      </div>
    </>
  );
};

DocumentModal2.defaultProps = {
  outputSelection: null,
  emailTo: [],
  title: 'Documents',
  recipientField: 'customer',
  contactField: 'customerContact',
  module: 'Sales',
  useCustomerDefaults: false,
  multiValue: false,
  allowEmailTo: true,
  allowEmail: false,
  noModal: false, //Override to avoid wrapping in a Modal even for a singular print.
  additionalInputVariables: {},
  allowOpenTillDrawer: false,
  allowResetToStart: false,
  delayBeforeSkip: false, //Should there be a delay before the 'skip' button is available on modals.
};

type IDocumentModal2WrapperProps = {
  modalOpen?: boolean;
  modalWrapper?: boolean;
  noModal?: boolean;
};

//Required as a pass-through to the picking instruction generate mutation, for the moment.
type IPickingInstructionDocumentModalProps = {
  generateSingle?: true;
};

type IDocumentModalProps = Omit<IDocumentModal2Props, 'documentConfig' | 'actionEntities' | 'onProcessSummary'> &
  IDocumentModal2WrapperProps &
  IPickingInstructionDocumentModalProps;

export default <PassThroughProps extends unknown>({
  modalWrapper,
  modalOpen,
  ...props
}: IDocumentModalProps & PassThroughProps) => {
  const actionEntities = useSelector((store: any) => store.app.actionEntities);
  const documentConfig = useDocumentConfig();
  const { onClose: closeFromContext } = useContext(ModalContext);
  const onClose = closeFromContext || props.onClose; //'props.onClose' to be deprecated, but there's still places using this approach currently.

  const onProcessPreview = useOnProcessPreview();

  //Hold off rendering until documentConfig is loaded - Makes things simpler within the component.
  if (!documentConfig) return null;

  const { modalStyles, noModal, multiValue } = props;

  const onProcessSummary = useOnProcessSummary(onClose);

  return (
    <ModalAndStyleWrapper
      modalStyles={modalStyles}
      modalOpen={modalOpen}
      modalWrapper={modalWrapper}
      noModal={noModal}
      multiValue={multiValue}
    >
      <DocumentModal2
        {...(props as any)}
        actionEntities={actionEntities}
        documentConfig={documentConfig}
        onProcessSummary={onProcessSummary}
        onProcessPreview={onProcessPreview}
      />
    </ModalAndStyleWrapper>
  );
};
